<template>
    <div class="full-height-dynamic safe-area-top flex flex-col xl:flex-row min-w-full relative" :class="[rootClass, isChatPage ? 'bg-white' : 'bg-surface']">
        <div class="flex flex-col md:flex-row w-full overflow-y-visible md:overflow-y-auto h-full">
            <a href="#main-content" class="absolute left-0 top-0 -translate-y-full focus:translate-y-0 bg-white z-50 p-2 border" @click="skipHandler">
                {{ t("layout.skipToMainContent") }}
            </a>
            <LeftSidebar v-if="sidebarRedesign" :panes-with-notifications="panesWithNotifications" :pane-notification-counts="paneNotificationCounts" />
            <LegacyLeftSidebar v-else :panes-with-notifications="panesWithNotifications" :pane-notification-counts="paneNotificationCounts" />
            <main id="main-content" ref="mainContainer" tabindex="-1" class="relative grow flex min-h-0 overflow-y-clip overflow-x-hidden">
                <div scroll-region class="relative flex flex-col grow overflow-y-auto overflow-x-hidden min-h-0" :class="{ 'bg-surface': !isChatPage }">
                    <ToastProvider>
                        <TeamInvitationContinuation :user="userDetails">
                            <!-- Chat pages opt out of the iOS push transition: their full-height flex layout
                             (scroll containers, max-h-full math, flex-end pinned controls) breaks when
                             wrapped in an extra flex container, causing ChatMessageControls to thrash
                             position on mount. Other pages get the slide. -->
                            <Transition v-if="isNative && !isChatPage" name="ios-push">
                                <div :key="page.url" class="flex flex-col grow bg-surface">
                                    <slot />
                                </div>
                            </Transition>
                            <slot v-else />
                        </TeamInvitationContinuation>
                        <ToastPortal defer to="html">
                            <ToastViewport class="fixed bottom-4 right-4 z-9999" />
                        </ToastPortal>
                    </ToastProvider>
                    <!-- In-flow spacer reserving room for the fixed mobile nav. A real flex child
                         (height honored at scroll-end by WebKit) rather than padding-bottom on this
                         scroll container, which iOS drops at scroll-end — see reserveNavSpace comment. -->
                    <div v-if="reserveNavSpace" aria-hidden="true" class="shrink-0" :style="{ height: 'var(--mobile-nav-total-height)' }" />
                    <!-- Sits outside the page Transition so it persists across navigations and doesn't slide with the page content. -->
                    <MobileMainMenu :pane-notification-counts="paneNotificationCounts" />
                </div>
            </main>
        </div>
    </div>
    <DevToolsLauncher />
    <FullPageLoading />
    <MasqueradeBanner />
</template>

<script setup lang="ts">
import { Capacitor } from "@capacitor/core";
import { usePage } from "@inertiajs/vue3";
import { useMediaQuery } from "@vueuse/core";
import { dismissNotificationLocallyKey, panesWithNotificationKey, refreshSidebarNotificationsKey, type UserDetails } from "~vue/appTypes";
import FullPageLoading from "~vue/components/FullPageLoading.vue";
import MasqueradeBanner from "~vue/components/MasqueradeBanner.vue";
import LeftSidebar from "~vue/components/navigation/LeftSidebar.vue";
import LegacyLeftSidebar from "~vue/components/navigation/legacy/LegacyLeftSidebar.vue";
import MobileMainMenu from "~vue/components/navigation/MobileMainMenu.vue";
import { useKeyboard } from "~vue/composables/useKeyboard";
import { useSidebarNotifications } from "~vue/composables/useSidebarNotifications";
import DevToolsLauncher from "~vue/DevTools/DevToolsLauncher.vue";
import { useSideBarStore } from "~vue/stores/sideBarStore";
import TeamInvitationContinuation from "~vue/Teams/TeamInvitationContinuation.vue";
import { ToastPortal, ToastProvider, ToastViewport } from "reka-ui";
import { computed, inject, nextTick, provide, type Ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const isNative = Capacitor.isNativePlatform();

const mainContainer = useTemplateRef("mainContainer");

const skipHandler = () => {
    nextTick(() => {
        mainContainer.value?.focus();
    });
};

const page = usePage();
const isChatPage = computed(() => page.url?.startsWith("/coach/") ?? false);
const userDetails = inject<Ref<UserDetails>>("user_details");
const sidebarRedesign = computed(() => userDetails?.value?.is_sidebar_redesign_enabled ?? false);
const rootClass = computed(() => (sidebarRedesign.value || useSideBarStore.isOpen ? "fixed overflow-y-hidden" : ""));

// Drop the mobile-nav bottom reservation while the soft keyboard is open —
// the nav is hidden in that state (see MobileMainMenu) so the reserved space
// is otherwise empty and creates a visible gap below the composer.
const { keyboardHeight } = useKeyboard();

// Reserve space for the fixed mobile nav so page content isn't hidden behind it.
// This is done with a real in-flow spacer element (see template) rather than
// padding-bottom on the scroll container: WebKit/iOS drops a flex-column
// overflow-y-auto container's padding-bottom at scroll-end, so the padding
// reserved no space and the last bit of content slipped behind the nav on the
// native app. A flex child's height is always honored. --mobile-nav-total-height
// is kept in sync with the bar's real rendered height by MobileMainMenu, and the
// spacer is gated to mobile widths since the nav is hidden from md up.
const isMobile = useMediaQuery("(max-width: 767.98px)");

// The nav hides while the soft keyboard is open, so drop the reservation then.
const reserveNavSpace = computed(() => isMobile.value && !keyboardHeight.value);

const { panesWithNotifications, paneNotificationCounts, dismissNotificationLocally, refresh } = useSidebarNotifications();
provide(panesWithNotificationKey, panesWithNotifications);
provide(refreshSidebarNotificationsKey, refresh);
provide(dismissNotificationLocallyKey, dismissNotificationLocally);
</script>
